/**
 * Mobile drawer (< xl). The hamburger toggles a full-height slide-in panel
 * with a member-first information architecture:
 *
 *   Primary (in-app member routes + external Stock):
 *     Stock · Order & winning-bid status · Winning-bid vehicles ·
 *     Favorite vehicles · Support · Account settings
 *   Secondary (corporate marketing site):
 *     Auction ▾ · Countries ▾ (accordions) · Pricing · About · Blog · Contact
 *   Account (bottom): user name · Sign out
 *
 * A11y / UX:
 *   - Toggle button exposes aria-expanded + aria-controls.
 *   - Escape, overlay click, or a route change closes the drawer.
 *   - Body scroll is locked while open.
 *   - The drawer is focused on open; focus returns to the toggle on close.
 *   - Only Auction / Countries expand as accordions; every other secondary
 *     entry is a flat link. Active in-app routes are highlighted.
 *   - Tap targets are ≥44px tall.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { BLOG_HREF, CONTACT_HREF, DEFAULT_NAV, SITE_HREF, STOCK_HREF } from './chromeNav';
import type { NavItem } from './DesktopMegaItem';

interface Labels {
  stock: string;
  auction: string;
  countries: string;
  pricing: string;
  about: string;
  blog: string;
  contact: string;
  toggleNav: string;
  signOut: string;
}

const DEFAULT_LABELS: Labels = {
  stock: 'Stock',
  auction: 'Auction',
  countries: 'Countries',
  pricing: 'Pricing',
  about: 'About',
  blog: 'Blog',
  contact: 'Contact',
  toggleNav: 'Toggle navigation menu',
  signOut: 'Sign out',
};

/** Primary in-app member routes. Mirrors AppShell's sidebar NAV_ITEMS so the
 *  drawer and desktop sidebar stay in lock-step. */
const PRIMARY_ROUTES: readonly { to: string; label: string }[] = [
  { to: '/orders', label: 'Order & winning-bid status' },
  { to: '/vehicles', label: 'Winning-bid vehicles' },
  { to: '/stock/favorites', label: 'Favorite vehicles' },
  { to: '/support', label: 'Support' },
  { to: '/account', label: 'Account settings' },
] as const;

function findNav(title: string): NavItem | undefined {
  return DEFAULT_NAV.find((n) => n.title.toLowerCase().startsWith(title.toLowerCase()));
}

export interface MobileNavUser {
  name?: string | null;
  email?: string | null;
  onSignOut?: () => void;
}

interface Props {
  /** Retained for API parity with Header; the drawer derives its own IA from
   *  PRIMARY_ROUTES + DEFAULT_NAV, so this is unused. */
  navItems?: NavItem[];
  labels?: Labels;
  /** When supplied, the drawer shows the user name + Sign out at the bottom. */
  user?: MobileNavUser;
  /** The "Stock" target (Header passes the signed-in hand-off). */
  stockHref?: string;
}

const ITEM_BASE =
  'flex items-center min-h-[44px] px-6 py-3 text-base font-medium text-foreground hover:bg-brand-50 transition-colors duration-150';

export function MobileNav({
  labels = DEFAULT_LABELS,
  user,
  stockHref = STOCK_HREF,
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<'auction' | 'countries' | null>(null);
  const { pathname } = useLocation();
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const panelId = useId();

  const auctionNav = findNav('Auction');
  const countriesNav = findNav('Countries');
  const pricingNav = findNav('Pricing');
  const aboutNav = findNav('About');

  const isActive = (to: string): boolean => pathname === to || pathname.startsWith(`${to}/`);

  // Close + reset when the route changes.
  useEffect(() => {
    setOpen(false);
    setExpanded(null);
  }, [pathname]);

  // Escape closes; body scroll lock; focus the drawer on open, restore focus
  // to the toggle on close.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    // Focus the drawer so keyboard users land inside it.
    drawerRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  function close(): void {
    setOpen(false);
    // Return focus to the toggle (WCAG 2.4.3).
    toggleRef.current?.focus();
  }

  function renderAccordion(
    key: 'auction' | 'countries',
    label: string,
    nav: NavItem | undefined,
  ): JSX.Element | null {
    if (!nav) return null;
    const isOpen = expanded === key;
    const sectionId = `${panelId}-${key}`;
    return (
      <div className="border-b border-border/50">
        <button
          type="button"
          onClick={() => setExpanded((cur) => (cur === key ? null : key))}
          className={`${ITEM_BASE} w-full justify-between text-left`}
          aria-expanded={isOpen}
          aria-controls={sectionId}
        >
          <span>{label}</span>
          <ChevronDown
            className={`w-5 h-5 text-foreground-tertiary transition-transform duration-150 ${
              isOpen ? 'rotate-180' : ''
            }`}
            aria-hidden="true"
          />
        </button>
        {isOpen ? (
          <div id={sectionId} className="bg-background-alt">
            <a
              href={nav.path}
              target="_blank"
              rel="noopener noreferrer"
              onClick={close}
              className={`${ITEM_BASE} pl-10 text-sm`}
            >
              View all {nav.title}
            </a>
            {nav.children?.map((child) => (
              <a
                key={child.path}
                href={child.path}
                target="_blank"
                rel="noopener noreferrer"
                onClick={close}
                className={`${ITEM_BASE} pl-10 text-sm`}
              >
                {child.title}
              </a>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  const overlay = open ? (
    <div className="site-chrome xl:hidden fixed inset-0 z-[100]">
      {/* Click-away overlay */}
      <button
        type="button"
        aria-label="Close navigation menu"
        onClick={close}
        className="absolute inset-0 bg-black/40 cursor-default"
      />
      <div
        ref={drawerRef}
        id={panelId}
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        tabIndex={-1}
        className="absolute inset-y-0 left-0 w-[86%] max-w-sm bg-white shadow-2xl overflow-y-auto outline-none mobile-panel-enter-forward"
      >
        {/* Primary member routes */}
        <nav aria-label="Primary" className="border-b border-border">
          <a
            href={stockHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
            className={`${ITEM_BASE} border-b border-border/50`}
          >
            {labels.stock}
          </a>
          {PRIMARY_ROUTES.map((item) => {
            const active = isActive(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={close}
                aria-current={active ? 'page' : undefined}
                className={`${ITEM_BASE} border-b border-border/50 ${
                  active ? 'bg-brand-50 text-brand-700 font-semibold' : ''
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Secondary — corporate marketing site */}
        <nav aria-label="More" className="border-b border-border">
          {renderAccordion('auction', labels.auction, auctionNav)}
          {renderAccordion('countries', labels.countries, countriesNav)}
          <a
            href={
              pricingNav?.path ?? `${SITE_HREF}/about-auction/detailed-provide-cars-import-process`
            }
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
            className={`${ITEM_BASE} border-b border-border/50`}
          >
            {labels.pricing}
          </a>
          <a
            href={aboutNav?.path ?? `${SITE_HREF}/about-provide-cars`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
            className={`${ITEM_BASE} border-b border-border/50`}
          >
            {labels.about}
          </a>
          <a
            href={BLOG_HREF}
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
            className={`${ITEM_BASE} border-b border-border/50`}
          >
            {labels.blog}
          </a>
          <a
            href={CONTACT_HREF}
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
            className={ITEM_BASE}
          >
            {labels.contact}
          </a>
        </nav>

        {/* Account area */}
        {user ? (
          <div className="bg-background-alt" data-testid="mobile-account-area">
            <div className="px-6 py-3 text-xs uppercase tracking-wide text-foreground-secondary">
              {user.name || user.email}
            </div>
            {user.onSignOut ? (
              <button
                type="button"
                onClick={() => {
                  user.onSignOut?.();
                  close();
                }}
                className={`${ITEM_BASE} w-full text-left border-t border-border/50`}
              >
                {labels.signOut}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        ref={toggleRef}
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-label={labels.toggleNav}
        aria-expanded={open}
        aria-controls={panelId}
        className="xl:hidden inline-flex items-center justify-center min-h-[44px] min-w-[44px] p-2 text-foreground order-first"
      >
        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>
      {overlay && typeof document !== 'undefined' ? createPortal(overlay, document.body) : null}
    </>
  );
}
